import { Box, Button, ClickAwayListener } from "@mui/material";
import { CSSProperties, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDraggable } from "../hooks/useDraggable";
import style from "./Modal.module.scss";

type ModalProps = {
  children?: React.ReactNode | React.ReactNode[];
  headerConfig?: {
    headerContent?: React.ReactNode | React.ReactNode[];
    hasBorder?: boolean;
    headerSubtitle?: string;
    hasBackground?: boolean;
  };
  onClose?: Function;
  onAccept?: Function;
  buttonsEnalbed?: boolean;
  buttons?: {
    name: string;
    callback: Function;
    class?: string[];
    style?: CSSProperties;
    disabled?: boolean;
  }[];
  hasOverlay?: boolean;
  isDraggable?: boolean;
  customCss?: CSSProperties;
  isResizable?: boolean;
  bodyCustomClass?: string;
  closeOnClickAway?: boolean;
  customWrapperCss?: CSSProperties;
};

const onCloseHandler = (onCancel?: Function) => {
  if (onCancel != null) {
    onCancel();
  } else {
    console.warn(
      "You are trying to call onClose function without passing it through Modal component"
    );
  }
};

export default function Modal({
  children,
  headerConfig,
  onClose,
  buttonsEnalbed = false,
  hasOverlay = true,
  isDraggable = false,
  customCss,
  buttons,
  isResizable = false,
  bodyCustomClass,
  closeOnClickAway = true,
  customWrapperCss,
}: ModalProps) {
  const refDrag = useRef<HTMLDivElement>(null);
  useDraggable(refDrag);

  const Modal = hasOverlay ? (
    <div
      style={customWrapperCss ?? undefined}
      className={style["wrapper-overlay"]}
    >
      <DialogCore
        isDraggable={isDraggable}
        headerConfig={headerConfig}
        onClose={onClose}
        buttonsEnalbed={buttonsEnalbed}
        isResizable={isResizable}
        customCss={customCss}
        buttons={buttons}
        bodyCustomClass={bodyCustomClass}
        closeOnClickAway={closeOnClickAway}
      >
        {children}
      </DialogCore>
    </div>
  ) : (
    <DialogCore
      isDraggable={isDraggable}
      headerConfig={headerConfig}
      onClose={onClose}
      buttonsEnalbed={buttonsEnalbed}
      isResizable={isResizable}
      customCss={customCss}
      buttons={buttons}
      bodyCustomClass={bodyCustomClass}
      closeOnClickAway={closeOnClickAway}
    >
      {children}
    </DialogCore>
  );

  return createPortal(Modal, document.body);
}

function DialogCore({
  isDraggable,
  headerConfig = { headerContent: "", hasBorder: true },
  onClose,
  children,
  buttonsEnalbed,
  customCss,
  isResizable,
  buttons,
  bodyCustomClass,
  closeOnClickAway,
}: Pick<
  ModalProps,
  | "isDraggable"
  | "headerConfig"
  | "onClose"
  | "children"
  | "buttonsEnalbed"
  | "customCss"
  | "isResizable"
  | "buttons"
  | "bodyCustomClass"
  | "closeOnClickAway"
>) {
  const refDrag = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  useDraggable(refDrag, headerRef);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (headerRef?.current && bodyRef?.current) {
      const header = headerRef.current;
      const body = bodyRef.current;
      const modal: any = header.parentNode;
      const modalDims = modal?.getBoundingClientRect();
      const headerDims = header?.getBoundingClientRect();
      const headerHeight = headerDims.height;
      const modalHeight = modalDims.height;

      const bodyHeight = modalHeight - headerHeight;
      const bodyHeightPercentage = `${(
        (bodyHeight / modalHeight) *
        100
      ).toFixed(2)}%`;

      body.style.height = bodyHeightPercentage;
    }
  });

  const { headerContent, hasBorder, headerSubtitle, hasBackground } =
    headerConfig;
  return (
    <ClickAwayListener
      onClickAway={(e) => {
        if (closeOnClickAway && onClose) {
          onClose();
        }
      }}
    >
      <Box
        boxShadow={5}
        className={isResizable ? style["main-resizable"] : style.main}
        style={{ ...customCss }}
        ref={isDraggable ? refDrag : null}
      >
        {(headerContent || headerSubtitle) && (
          <div ref={headerRef} style={isDraggable ? { cursor: "grab" } : {}}>
            <div
              className={`${
                hasBorder ? style.modalHeaderBorder : style.modalHeader
              } ${hasBackground ? style.modalHeaderColored : ""}`}
            >
              {headerContent}
            </div>
            <div style={{ padding: "0px 10px" }}>{headerSubtitle}</div>
            <span
              className="dijitDialogCloseIcon"
              style={{ top: 5 }}
              onClick={() => onCloseHandler(onClose)}
            />
          </div>
        )}
        <div ref={bodyRef} className={`${style.body} ${bodyCustomClass}`}>
          {children}
        </div>
        {buttonsEnalbed && (
          <form className={style.btns}>
            {buttons &&
              buttons.map((btn, index) => (
                <Button
                  size="small"
                  disabled={btn.disabled ?? false}
                  key={index}
                  sx={btn.style}
                  // className={btn.class ? btn.class?.join(" ") : "tFormButton"}
                  onClick={(e) => {
                    e.preventDefault();
                    btn.callback();
                  }}
                >
                  {btn.name}
                </Button>
              ))}
          </form>
        )}
      </Box>
    </ClickAwayListener>
  );
}
